import { CloseOutlined, HomeOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, FormGroup, Icon, Input, Text } from 'components';

import { message, Select } from 'antd';
import { FormGroupItem } from 'components/FormGroupItem';

import {
  accountSelector,
  partnerSelector,
  partnersSelector,
} from 'features/merchants/redux';
import { FirebaseCollectionsType } from 'firebaseAPI';
import { useSelector } from 'react-redux';
import { useFirestore } from 'react-redux-firebase';
import { styled } from 'theme';
import type { Firebase } from 'types/firebase';
import { getOptionValueText } from 'utils/OptionUtils';
import { MERCHANT_PAYMENT_TYPE } from '../../../../constants';
/**
 * Styling
 */
const Container = styled.div``;

/**
 * Types
 */
export type MerchantTypeProps = {
  merchant: Firebase.Merchant;
  onSave: (name: string, value: string | number) => void;
  onUpdateEcomId: (value: string) => Promise<void>;
};

type FormFieldType = {
  title: string;
  icon: React.ReactNode;
  inputs: {
    type?: string;
    label: string;
    alternativeLabel: string;
    name: string;
    value: string | number | null;
  }[];
};

function mapToSelectOptions(map: Record<string, Firebase.Partner>) {
  return (
    Object.values(map)
      // @ts-ignore
      .sort((a, b) => a?.name?.localeCompare(b?.name)) // Sort alphabetically by `name`
      .map((item) => ({
        label: item.name,
        value: item.id,
      }))
  );
}

/**
 * MerchantType component
 */
const MerchantType: React.FC<MerchantTypeProps> = (props) => {
  /* Props */
  const { merchant } = props;
  /* Hooks */
  const firestore = useFirestore();
  const partners = useSelector(partnersSelector);
  const account = useSelector(accountSelector(props.merchant.accountId));
  const currentPartner = useSelector(partnerSelector(account?.partnerId));
  const merchantTypeValue =
    getOptionValueText(MERCHANT_PAYMENT_TYPE, merchant?.merchantPaymentType) ??
    'NOT SET';
  const canAddEcomId = !!merchant?.campaign && merchant?.clearhausEcomApiKey;
  /* Local state */
  const [partnerIdValue, setNewPartnerValue] = useState<string | undefined>(
    undefined,
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [ecomValue, setNewEcomValue] = useState<string | undefined>(undefined);
  const [isEcomEdit, setIsEcomEdit] = useState<boolean>(false);
  /**
   * Create partner list
   */
  const PARTNER_LIST = useMemo(() => {
    if (partners) {
      const list = mapToSelectOptions(partners);
      return list;
    } else {
      return [];
    }
  }, [partners]);
  /**
   * _onHandleUpdatePartner
   * We opt out of FormGroupItem as we need to update account and not merchant
   */
  const _onHandleUpdatePartner = async () => {
    try {
      if (partnerIdValue || partnerIdValue === null) {
        await firestore
          .collection(FirebaseCollectionsType.VIBRANT_API_ACCOUNTS)
          .doc(props.merchant.accountId)
          .update({
            partnerId: partnerIdValue,
          });
        message.success('Partner updated');
        setIsEdit(false);
      }
    } catch (e) {
      message.error('Failed to update partner');
    }
  };
  /**
   * onDropdownHandleChange
   */
  const onDropdownHandleChange = (value: string) => {
    setNewPartnerValue(value);
  };
  // @ts-ignore
  const formFields = useMemo<FormFieldType[]>(() => {
    return [
      {
        title: 'Merchant Type',
        icon: <HomeOutlined />,
        inputs: [
          {
            type: 'dropdown',
            label: 'Merchant Type',
            alternativeLabel: 'Merchant types',
            name: 'merchantPaymentType',
            value: merchantTypeValue,
            dropdown: {
              placeholder: 'Select a type',
              values: MERCHANT_PAYMENT_TYPE,
              options: MERCHANT_PAYMENT_TYPE,
            },
          },
          // {
          //   type: 'input',
          //   label: 'Clearhaus Ecom Merchant Id (campaign must be set)',
          //   alternativeLabel: 'Clearhaus Ecom Merchant Id',
          //   name: 'clearhausEcomMerchantId',
          //   restrictEdit: !canAddEcomId,
          //   value: merchant?.clearhausEcomMerchantId ?? 'Not set',
          // },
          {
            type: 'input',
            label: 'Clearhaus Ecom API Key',
            alternativeLabel: 'Clearhaus Ecom API Key',
            name: 'clearhausEcomApiKey',
            value: merchant?.clearhausEcomApiKey ?? 'Not set',
          },
        ],
      },
    ];
  }, [merchant]);
  /**
   * Update default partner id
   */
  useEffect(() => {
    if (currentPartner?.id) {
      setNewPartnerValue(currentPartner?.id);
    }
  }, [currentPartner?.id]);

  const handleToggleEditing = () => {
    setIsEdit((isEditingPrev) => !isEditingPrev);
  };

  const resetValue = () => {
    if (currentPartner?.id) {
      setNewPartnerValue(currentPartner?.id);
    } else {
      setNewPartnerValue(undefined);
    }
  };
  /**
   * Ecom handlers
   */
  const _onHandleEcomIdUpdate = async () => {
    if (!ecomValue) {
      return;
    }
    await props.onUpdateEcomId(ecomValue);
    handleToggleEcomEditing();
    resetEcomValue();
  };
  const handleToggleEcomEditing = () => {
    setIsEcomEdit((isEditingPrev) => !isEditingPrev);
  };
  const resetEcomValue = () => {
    setNewEcomValue(undefined);
  };

  return (
    <div>
      {formFields.map((form) => (
        <Container key={form.title}>
          <FormGroup icon={form.icon} title={form.title}>
            {form.inputs.map((input) => (
              <FormGroupItem
                {...input}
                key={input.name}
                // @ts-ignore
                onSave={(newValue) => props.onSave(input.name, newValue)}
              />
            ))}
            {/* Clearhaus Ecom Merchant Id */}
            <Box mt="24px">
              <Text size={isEcomEdit ? 'ui2' : 'ui3'} bold={isEcomEdit}>
                Clearhaus Ecom Merchant Id (campaign must be set)
              </Text>
            </Box>
            {!isEcomEdit && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Text size="ui2">
                  {merchant?.clearhausEcomMerchantId ?? 'Not set'}
                </Text>
                <Button
                  disabled={!canAddEcomId}
                  style={{ color: '#FF8A48' }}
                  type="text"
                  onClick={handleToggleEcomEditing}
                >
                  {canAddEcomId ? 'Change' : ' '}
                </Button>
              </Box>
            )}
            {isEcomEdit && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Box display="flex" justifyContent="space-between" mb="8px">
                  <Box />
                  <Box marginTop="-28px" mb="24px">
                    <Button
                      ghost
                      onClick={() => {
                        handleToggleEcomEditing();
                        resetEcomValue();
                      }}
                    >
                      <Icon icon={<CloseOutlined />} />
                    </Button>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Input
                    name="clearhausEcomMerchantId"
                    defaultValue={
                      merchant?.clearhausEcomMerchantId ?? undefined
                    }
                    value={ecomValue}
                    onChange={(e) => setNewEcomValue(e.currentTarget.value)}
                  />
                  <Button type="primary" onClick={_onHandleEcomIdUpdate}>
                    Save
                  </Button>
                </Box>
              </Box>
            )}
            {/* Partner */}
            <Box mt="24px">
              <Text size={isEdit ? 'ui2' : 'ui3'} bold={isEdit}>
                Partner
              </Text>
            </Box>

            {!isEdit && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Text size="ui2">
                  {currentPartner?.name && currentPartner?.name}
                  {account?.partnerId === 'NO_PARTNER' && 'NO PARTNER'}
                  {!currentPartner?.name && !account?.partnerId && 'NOT SET'}
                </Text>
                <Button
                  style={{ color: '#FF8A48' }}
                  type="text"
                  onClick={handleToggleEditing}
                >
                  Change
                </Button>
              </Box>
            )}
            {isEdit && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Box display="flex" justifyContent="space-between" mb="8px">
                  <Box />
                  <Box marginTop="-28px" mb="24px">
                    <Button
                      ghost
                      onClick={() => {
                        handleToggleEditing();
                        resetValue();
                      }}
                    >
                      <Icon icon={<CloseOutlined />} />
                    </Button>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Select
                    style={{ width: '100%', maxWidth: '300px' }}
                    showSearch
                    optionFilterProp="children"
                    placeholder={'Choose partner'}
                    onSelect={onDropdownHandleChange}
                    value={partnerIdValue}
                    defaultValue={currentPartner?.id ?? undefined}
                  >
                    {PARTNER_LIST.map((opt) => {
                      if (!opt?.value) {
                        return;
                      }
                      return (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      );
                    })}
                    <Select.Option key={-1} value={'NO_PARTNER'}>
                      No partner
                    </Select.Option>
                  </Select>
                  <Button type="primary" onClick={_onHandleUpdatePartner}>
                    Save
                  </Button>
                </Box>
              </Box>
            )}
          </FormGroup>
        </Container>
      ))}
    </div>
  );
};

export { MerchantType };
